import ApiService from "./apiService";
import ElliHost from "./elliHost";
import { setOriginVals } from "../state/originSlice";
import { initAppStateFromOrigin, setAuthToken } from "../state/appSlice";
import { store } from "../state/store"
import { AxiosResponse } from "axios";
import { IInitAppState } from "../models/IInitAppState";
import { isEncompassAdmin } from "../utils/personaHelper";

const baseURL = process.env.REACT_APP_EPC_API as string; // ToDo: this should be fetched from the config

class OriginService {

    private static instance: OriginService;
    private readonly apiService: ApiService;
    private readonly elliHost;
    private buildOriginPromise: Promise<any> | null = null;

    private constructor() {
        this.apiService = ApiService.getInstance(baseURL);
        this.elliHost = ElliHost.getInstance();
        
        // Set up the token refresh callback globally for all API service instances
        ApiService.setGlobalTokenRefreshCallback(this.refreshOriginData.bind(this));
    }

    public static getInstance(): OriginService {
        if (!OriginService.instance) {
            OriginService.instance = new OriginService();
        }
        return OriginService.instance;
    }

    private async getOriginApi(elliPat: string, originId: string): Promise<AxiosResponse<unknown, any>> {
        const apiOrigin = await this.apiService.get(`origins/${originId}`, {
            headers: {
                "X-Eli-Pat": elliPat
            }
        }).then((res) => {
            if (res.status < 200 && res.status > 299) {
                throw new Error();
            }
            
           return res;
        });
        return apiOrigin;
    }

    public async buildOrigin(): Promise<any> {
        // If buildOrigin is already in progress, return the existing promise
        if (this.buildOriginPromise) {
            return this.buildOriginPromise;
        }

        // Create and store the promise
        this.buildOriginPromise = this.executeBuildOrigin();

        try {
            const result = await this.buildOriginPromise;
            return result;
        } finally {
            // Clear the promise when done (success or failure)
            this.buildOriginPromise = null;
        }
    }

    private async executeBuildOrigin(): Promise<any> {
        const host: any = await this.elliHost.getOrigin();        
        
        const isTPOConnectTraffic = !!(host?.settings?.TPOAdminSettings);
        
        store.dispatch(setOriginVals({ 
            pat: host.partnerAccessToken, 
            id: host.id, 
            isTPOConnectTraffic: isTPOConnectTraffic 
        }));
        
        const origin: any = await this.getOriginApi(host.partnerAccessToken, host.id)
        
        // Check if user is admin before calling getPersonas
        const userPersonas = origin?.data?.result?.pointOfContact?.personas as Array<string> ?? [];
        const isUserAdmin = isEncompassAdmin(userPersonas);
        
        const personas = isUserAdmin ? await this.elliHost.getPersonas() : userPersonas;
        let entityId = origin?.data?.result?.entityId as string;
        localStorage.setItem("encompassInstanceId", entityId.split(':')[3]);

        store.dispatch(initAppStateFromOrigin({
            authToken: origin?.data?.result?.oAuthToken?.accessToken as string ?? null,
            authExpires: origin?.data?.result?.oAuthToken?.expirationDateTimeUTC as string ?? null,
            configId: origin?.data?.result?.configId as string ?? null,
            personas: userPersonas,
            loanID: origin?.data?.result?.loan?.id as string ?? null,
            encompassPersonas: personas ?? [],
            loanNumber: origin?.data?.result?.loan?.loanNumber as string ?? null,
            borrowerName: origin?.data?.result?.loan?.applications?.[0]?.borrower ? 
                `${origin?.data?.result?.loan?.applications[0].borrower.firstName || ''} ${origin?.data?.result?.loan?.applications[0].borrower.lastName || ''}`.trim() : null,
            userId: origin?.data?.result?.pointOfContact?.email as string ?? null,
            userFullName: origin?.data?.result?.pointOfContact?.name as string ?? null,
            fieldIdValuePairs : origin?.data?.result?.fieldIdValuePair as any ?? null,
            borrowerPairIds: origin?.data?.result?.loan?.applications?.map((app: any) => app.id) ?? null,
            isAdmin: isUserAdmin
        } as IInitAppState))

        return origin;
    }

    public async refreshOriginData(): Promise<void> {
        try {

            const host: any = await this.elliHost.getOrigin();
            const origin: any = await this.getOriginApi(host.partnerAccessToken, host.id);

            // Update the auth token and expiration time
            store.dispatch(setAuthToken({
                authToken: origin?.data?.result?.oAuthToken?.accessToken as string ?? null,
                authExpires: origin?.data?.result?.oAuthToken?.expirationDateTimeUTC as string ?? null
            }));

        } catch (error) {
            throw error;
        }
    }

};

export default OriginService;


